import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import urls from 'urls';
import {valid_email as validateEmail} from '~/common/utils';
import {Typography} from '~library/atoms/Typography';
import {Link} from '~library/atoms/Link';
import {Video} from '~library/atoms/Video';
import {LinkIcon} from '~library/svg/LinkIcon';
import {SocialWaIcon} from '~library/svg/SocialWaIcon';
import {SocialFbIcon} from '~library/svg/SocialFbIcon';
import {SocialIgIcon} from '~library/svg/SocialIgIcon';
import {CheckmarkIcon} from '~library/svg/CheckmarkIcon';
import {MailIcon} from '~library/svg/MailIcon';
import {Button} from '~library/atoms/Button';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {Input} from '~library/atoms/Input';
import {VendorLogos} from '~/common/vendors/VendorLogos';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {
  selectSheetId,
  selectShortLink,
  selectWishlistOption,
} from '~/SignUpSheets/reducers/sheet/selectors';

import {largishAndUpQuery, coin} from '~libSass/base/_exports.sass';
import './HostConfirm.sass';

export const HostConfirm = () => {
  const [copied, setCopied] = useState(false);
  const [sweepsInputValue, setSweepsInputValue] = useState('');
  const [sweepsOptOutError, setSweepsOptOutError] = useState('');
  const [sweepsOptOutSuccessMessage, setSweepsOptOutSuccessMessage] = useState('');
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const {matches: isDesktop} = useMatchQuery(`(${largishAndUpQuery})`);
  const sheetId = useSelector(selectSheetId);
  const shortLink = useSelector(selectShortLink);
  const wishlistOption = useSelector(selectWishlistOption);
  const hasDefaultWishlist = wishlistOption === 'evite-wishlist';

  const sweepsEnabled = evite.features.isEnabled('sus_sweeps_2024', false);
  const userHasOptedOutOfThisSweeps = window.optout_services.indexOf('sweep_stakes_2024') !== -1;
  const showSweepsToUser =
    !userHasOptedOutOfThisSweeps && !window.do_not_sell && !window.do_not_contact;

  /* Ensuring browser has finished rendering before we try to scroll to top */
  useEffect(() => {
    const handleScrollToTop = () => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    };
    const animationFrameId = requestAnimationFrame(handleScrollToTop);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(shortLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((error) => {
        evite.error('Failed to copy:', error);
      });
  }, [shortLink]);

  const openSweepsOptOutModal = useCallback(() => setShowOptOutModal(true), [setShowOptOutModal]);
  const handleOptOutInputChange = useCallback((e) => {
    setSweepsInputValue(e.target.value);
    setSweepsOptOutError('');
    setSweepsOptOutSuccessMessage('');
  }, []);

  const url = urls.get('sweeps_optout');
  const body = JSON.stringify({
    email: sweepsInputValue,
    user_id: window.user_id,
    optout: 'sweep_stakes_2024',
  });
  const submitSweepsOptOut = () => {
    evite
      .fetch(url, {
        method: 'post',
        body,
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
      .then((response) => response.clone().json())
      .then(() => {
        setSweepsOptOutSuccessMessage('You have opted out of the sweepstakes');
        setTimeout(() => setShowOptOutModal(false), 2000);
      })
      .catch((error) => {
        evite.error(`Error opting user out of SuS Sweepstakes: ${error}`);
      });
  };

  const handleSweepsOptOut = useCallback(() => {
    if (validateEmail(sweepsInputValue).is_valid) {
      submitSweepsOptOut();
    } else {
      setSweepsOptOutError(validateEmail(sweepsInputValue).error_message);
    }
  }, [sweepsInputValue, setSweepsOptOutError]);

  const handleCancelOptOut = useCallback(() => {
    setSweepsOptOutError('');
    setShowOptOutModal(false);
  }, [setSweepsOptOutError, setShowOptOutModal]);

  const CopyButton = useMemo(
    () => (
      <Button
        size={isDesktop ? 'large' : 'small'}
        variant={copied ? 'secondary' : 'primary'}
        className="confirm-page__copy-share-btn"
        onClick={handleCopy}
        icon={copied ? <CheckmarkIcon ratio={0.5} /> : null}
        data-qa-id="confirm-page-copy-link-btn"
      >
        {copied ? 'Copied!' : 'Copy link'}
      </Button>
    ),
    [copied, isDesktop, handleCopy]
  );

  return (
    <div className="sign-up-sheets__confirm-page">
      <div className="confirm-page__header">
        <Typography variant={isDesktop ? 'header1' : 'header2'} as="h1">
          Congratulations!
        </Typography>
        <Typography className="confirm-page__subtitle" variant="paragraph2" color="pebble">
          Your sign up sheet has been created.
          <Link data-qa-id="confirm-page-view-sheet-link" href={`/signup-sheet/${sheetId}`}>
            View sheet
          </Link>
        </Typography>
        {sweepsEnabled && showSweepsToUser ? (
          <Typography variant="paragraph2" color="pebble">
            Share this sheet to be entered into our Clear the List Sweepstakes for your chance to
            <Typography variant="paragraph2" as="span" color="charcoal" semibold>
              {' '}
              win $200 in eGift cards.
            </Typography>
            *
          </Typography>
        ) : null}
      </div>
      <div className="confirm-page__section">
        <Typography variant={isDesktop ? 'header2' : 'header3'} as="h2">
          Share your sheet
        </Typography>
        <div className="confirm-page__copy-share-url">
          {shortLink ? (
            <Typography
              className="confirm-page__copy-share-text"
              variant="paragraph1"
              data-qa-id="sharable-link"
            >
              {shortLink}
            </Typography>
          ) : null}
          {isDesktop ? CopyButton : null}
        </div>
        {!isDesktop ? CopyButton : null}
        <div className="confirm-page__social-links">
          <LinkIcon color={coin} ratio={0.9} />
          <MailIcon color={coin} ratio={0.9} />
          <SocialWaIcon color={coin} ratio={0.9} />
          <SocialFbIcon color={coin} ratio={0.7} />
          <SocialIgIcon color={coin} ratio={0.7} />
        </div>
      </div>

      {hasDefaultWishlist && (
        <div className="confirm-page__section">
          <Typography variant={isDesktop ? 'header2' : 'header3'} as="h2">
            Add your wishlist
          </Typography>
          <Typography variant="paragraph2" color="pebble">
            Add a wishlist from any retailer
          </Typography>
          <Video
            className="confirm-page__wishlist-url-anim"
            src="https://g0.evitecdn.com/signup-sheets/success-page-wishlist-url-animation.mp4"
          />
          <VendorLogos noGap />
          <Link
            variant={isDesktop ? 'large' : 'small'}
            as="secondary"
            data-qa-id="confirm-page-add-wishlist-btn"
            href={`/signup-sheet-maker/${sheetId}/edit#signup-sheet__wishlist-section`}
          >
            Add now
          </Link>
        </div>
      )}

      {sweepsEnabled && showSweepsToUser ? (
        <>
          <Typography className="sweeps-rules" variant="paragraph3" color="coin">
            *If you want to opt out of the Sweepstakes,{' '}
            <Button
              onClick={openSweepsOptOutModal}
              variant="transparent"
              data-qa-id="sweeps-opt-out"
              className="sweeps-rules__link"
            >
              please submit your email here
            </Button>
            . <br />
            NO PURCHASE NECESSARY. The Clear the List Sweepstakes is open only to legal residents of
            the 50 United States (and DC) who are at least eighteen (18) years old at the time of
            entry. Sweepstakes starts on July 1, 2024 at 12:01 a.m. ET and ends on October 1, 2024
            at 11:59 p.m. ET. For odds of winning, prize details and Official Rules visit
            https://content.evite.com/pages/sweepstakes-terms-conditions-2. Void where prohibited.
            Sponsored by Evite, Inc., 310 E Colorado St, Glendale, CA 91205. <br />
            <Link
              href="https://content.evite.com/pages/sweepstakes-terms-conditions-2"
              data-qa-id="sus-sweeps-rules"
              as="transparent"
              className="sweeps-rules__link"
            >
              Official terms & conditions
            </Link>
          </Typography>
          <ResponsiveModal
            onClose={handleCancelOptOut}
            isOpen={showOptOutModal}
            title="Opt out of the Clear the List Sweepstakes"
          >
            <Typography className="sweeps-rules__modal-copy" variant="paragraph2" color="pebble">
              Enter your email and you will no longer be eligible to enter for a chance to win.
            </Typography>
            <Input
              value={sweepsInputValue}
              onChange={handleOptOutInputChange}
              error={sweepsOptOutError}
              success={sweepsOptOutSuccessMessage}
              data-qa-id="sweeps-opt-out-input"
              placeholder="Enter email here"
            />

            <div className="sweeps-rules__button-row responsive-modal-button-row">
              <Button
                onClick={handleCancelOptOut}
                variant="transparent"
                className="sweeps-rules__modal-cancel"
                data-qa-id="sweeps-opt-out-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSweepsOptOut}
                variant="primary"
                size="large"
                data-qa-id="sweeps-opt-out-cta"
                disabled={!sweepsInputValue}
              >
                Opt out
              </Button>
            </div>
          </ResponsiveModal>
        </>
      ) : null}
    </div>
  );
};
